import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

import Button from '../../components/ui/Button';
import BulkActionBar from '../../components/ui/BulkActionBar';
import AuditFilters from './components/AuditFilters';
import AuditTable from './components/AuditTable';
import AuditSummary from './components/AuditSummary';
import AuditDetailsModal from './components/AuditDetailsModal';
import ExportModal from './components/ExportModal';

const AuditTrail = () => {
  const [filters, setFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    actionType: '',
    user: '',
    field: '',
    invoiceReference: ''
  });

  const [selectedEntries, setSelectedEntries] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showSummary, setShowSummary] = useState(true);

  const { user, currentOrgId } = useAuth();
  const [auditEntries, setAuditEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const [summaryData, setSummaryData] = useState(null);

  // Fetch audit entries from Supabase
  useEffect(() => {
    const fetchAudit = async () => {
      if (!user?.id || !currentOrgId) return;
      setLoading(true);
      try {
        let { data, error } = await supabase
          .from('audit_log')
          .select(`*, user_profiles(full_name, email)`) // join for display name/email
          .eq('org_id', currentOrgId)
          .order('timestamp', { ascending: false });
        if (error) throw error;

        const diffSnapshots = (before, after) => {
          try {
            if (!before || !after) return { fieldsChanged: [], changes: [] };
            const changes = [];
            const pushChange = (field, oldV, newV) => {
              if (oldV === undefined && newV === undefined) return;
              const oldS = oldV === null || oldV === undefined ? '' : String(oldV);
              const newS = newV === null || newV === undefined ? '' : String(newV);
              if (oldS !== newS) changes.push({ field, oldValue: oldS, newValue: newS });
            };
            // Scalars we care about
            const scalarKeys = [
              'vendorName','vendorAddress','vendorVatNumber',
              'invoiceNumber','invoiceDate','dueDate','currency','vendorId'
            ];
            scalarKeys.forEach(k => pushChange(k, before[k], after[k]));
            // Line items by index — capture description/qty/unit/vrate/net/vat/total
            const bLines = Array.isArray(before.lineItems) ? before.lineItems : [];
            const aLines = Array.isArray(after.lineItems) ? after.lineItems : [];
            if (bLines.length !== aLines.length) {
              pushChange('line_items.count', bLines.length, aLines.length);
            }
            const len = Math.min(bLines.length, aLines.length);
            for (let i=0; i<len; i++) {
              const b = bLines[i] || {}; const a = aLines[i] || {};
              pushChange(`line_items[${i}].description`, b.description, a.description);
              pushChange(`line_items[${i}].quantity`, b.quantity, a.quantity);
              pushChange(`line_items[${i}].unitPrice`, b.unitPrice, a.unitPrice);
              pushChange(`line_items[${i}].vatRate`, b.vatRate, a.vatRate);
              pushChange(`line_items[${i}].vatCode`, b.vatCode, a.vatCode);
              pushChange(`line_items[${i}].netTotal`, b.netTotal, a.netTotal);
              pushChange(`line_items[${i}].vatAmount`, b.vatAmount, a.vatAmount);
              pushChange(`line_items[${i}].total`, b.total, a.total);
            }
            const fieldsChanged = [...new Set(changes.map(c => c.field))];
            return { fieldsChanged, changes };
          } catch { return { fieldsChanged: [], changes: [] }; }
        };

        const baseEntries = (data || []).map((row) => {
          const meta = row?.meta_json || {};
          const fieldsChanged = Array.isArray(meta?.fieldsChanged)
            ? meta.fieldsChanged
            : (Array.isArray(meta?.changes) ? meta.changes.map(c => c.field).filter(Boolean) : []);
          const changes = Array.isArray(meta?.changes)
            ? meta.changes.map(c => ({
                field: c.field,
                oldValue: c.oldValue,
                newValue: c.newValue
              }))
            : [];

          const userLabel = meta?.user_email
            || meta?.edited_by
            || meta?.user
            || row?.user_profiles?.full_name
            || row?.user_profiles?.email
            || 'User';

          // If no explicit changes, try to compute from before/after in manual_edit_meta
          let computedFields = fieldsChanged;
          let computedChanges = changes;
          if ((!changes || changes.length === 0) && (meta.before || meta.after)) {
            const d = diffSnapshots(meta.before, meta.after);
            computedFields = d.fieldsChanged;
            computedChanges = d.changes;
          }

          return {
            id: row.id,
            timestamp: row.timestamp || row.created_at,
            user: userLabel,
            action: row.action || 'activity',
            invoiceReference: meta?.invoice_number || row.entity_id,
            description: meta?.description || meta?.message || '',
            fieldsChanged: computedFields,
            changes: computedChanges,
            sessionId: meta?.session_id || '',
            ipAddress: meta?.ip || '',
            userAgent: meta?.user_agent || '',
            entityType: row?.entity_type,
            entityId: row?.entity_id,
            vendorName: meta?.vendor_name || ''
          };
        });

        // Enrich with invoice number/vendor name for parsed_invoices
        const invoiceIds = baseEntries
          .filter(e => (e.entityType === 'parsed_invoices') && e.entityId)
          .map(e => e.entityId);
        let byId = {};
        if (invoiceIds.length) {
          const { data: invs, error: invErr } = await supabase
            .from('parsed_invoices')
            .select('id, invoice_number, vendor_name')
            .in('id', invoiceIds);
          if (!invErr && invs) {
            invs.forEach(i => { byId[i.id] = i; });
          }
        }

        const entries = baseEntries.map(e => {
          if (e.entityType === 'parsed_invoices' && byId[e.entityId]) {
            const i = byId[e.entityId];
            return {
              ...e,
              invoiceReference: i.invoice_number || e.invoiceReference,
              vendorName: i.vendor_name || e.vendorName
            };
          }
          return e;
        });

        setAuditEntries(entries);

        // Build simple summary
        const byAction = entries.reduce((acc, e) => {
          acc[e.action] = (acc[e.action] || 0) + 1; return acc;
        }, {});
        const byUser = entries.reduce((acc, e) => {
          const key = e.user || 'User';
          acc[key] = (acc[key] || 0) + 1; return acc;
        }, {});
        setSummaryData({
          totalEntries: entries.length,
          activeUsers: Object.keys(byUser).length,
          todayEntries: entries.filter(e => new Date(e.timestamp).toDateString() === new Date().toDateString()).length,
          weekEntries: entries.filter(e => (Date.now() - new Date(e.timestamp).getTime()) < 7*24*3600*1000).length,
          actionBreakdown: Object.entries(byAction).map(([type, count]) => ({ type, count })),
          mostActiveUsers: Object.entries(byUser).map(([name, activityCount]) => ({ name, email: name, activityCount }))
        });
      } catch (e) {
        console.error('Failed to fetch audit log:', e);
        setAuditEntries([]);
        setSummaryData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchAudit();
  }, [user?.id, currentOrgId]);

  // Filter audit entries based on current filters
  const filteredEntries = auditEntries?.filter(entry => {
    if (filters?.search && !entry?.description?.toLowerCase()?.includes(filters?.search?.toLowerCase()) &&
        !entry?.invoiceReference?.toLowerCase()?.includes(filters?.search?.toLowerCase()) &&
        !entry?.user?.toLowerCase()?.includes(filters?.search?.toLowerCase()) &&
        !(entry?.vendorName || '').toLowerCase()?.includes(filters?.search?.toLowerCase())) {
      return false;
    }
    
    if (filters?.dateFrom && new Date(entry.timestamp) < new Date(filters.dateFrom)) {
      return false;
    }
    
    if (filters?.dateTo && new Date(entry.timestamp) > new Date(filters.dateTo + 'T23:59:59')) {
      return false;
    }
    
    if (filters?.actionType && entry?.action !== filters?.actionType) {
      return false;
    }
    
    if (filters?.user && entry?.user !== filters?.user) {
      return false;
    }
    
    if (filters?.field && !entry?.fieldsChanged?.includes(filters?.field)) {
      return false;
    }
    
    if (filters?.invoiceReference && !entry?.invoiceReference?.toLowerCase()?.includes(filters?.invoiceReference?.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  // Sort filtered entries
  const sortedEntries = [...filteredEntries]?.sort((a, b) => {
    const aValue = a?.[sortConfig?.key];
    const bValue = b?.[sortConfig?.key];
    
    if (sortConfig?.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleViewDetails = (entry) => {
    setSelectedEntry(entry);
    setIsDetailsModalOpen(true);
  };

  const handleSelectAll = () => {
    setSelectedEntries(sortedEntries?.map(entry => entry?.id));
  };

  const handleClearSelection = () => {
    setSelectedEntries([]);
  };

  const handleBulkExport = () => {
    setIsExportModalOpen(true);
  };

  const handleExport = async (config) => {
    setIsExporting(true);
    
    // Simulate export process
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // In a real app, this would generate and download the file
    console.log('Exporting audit trail with config:', config);
    
    setIsExporting(false);
    setSelectedEntries([]);
  };

  const bulkActions = [
    {
      label: 'Export Selected',
      icon: 'Download',
      onClick: handleBulkExport,
      primary: true
    },
    {
      label: 'Generate Report',
      icon: 'FileText',
      onClick: () => console.log('Generate report for selected entries'),
      variant: 'outline'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto p-6">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Audit Trail</h1>
              <p className="text-muted-foreground mt-2">
                Comprehensive change tracking and compliance reporting
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowSummary(!showSummary)}
                iconName={showSummary ? "EyeOff" : "Eye"}
                iconPosition="left"
              >
                {showSummary ? 'Hide' : 'Show'} Summary
              </Button>
              
              <Button
                variant="default"
                onClick={() => setIsExportModalOpen(true)}
                iconName="Download"
                iconPosition="left"
              >
                Export Audit Trail
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className={`space-y-6 ${showSummary ? 'xl:col-span-3' : 'xl:col-span-4'}`}>
              {/* Bulk Action Bar */}
              {selectedEntries?.length > 0 && (
                <BulkActionBar
                  selectedCount={selectedEntries?.length}
                  totalCount={sortedEntries?.length}
                  actions={bulkActions}
                  onSelectAll={handleSelectAll}
                  onClearSelection={handleClearSelection}
                  position="sticky"
                />
              )}

              {/* Filters */}
              <AuditFilters
                filters={filters}
                onFiltersChange={setFilters}
                onExport={() => setIsExportModalOpen(true)}
                isExporting={isExporting}
              />

              {/* Results Summary */}
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  Showing {sortedEntries?.length} of {auditEntries?.length} audit entries
                </span>
                <div className="flex items-center space-x-4">
                  <span>
                    Last updated: {new Date()?.toLocaleString('en-GB')}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="RefreshCw"
                    onClick={() => window.location?.reload()}
                  >
                    Refresh
                  </Button>
                </div>
              </div>

              {/* Audit Table */}
              <AuditTable
                auditEntries={sortedEntries}
                selectedEntries={selectedEntries}
                onSelectionChange={setSelectedEntries}
                onViewDetails={handleViewDetails}
                onSort={handleSort}
                sortConfig={sortConfig}
              />
            </div>

            {/* Summary Panel */}
            {showSummary && (
              <div className="xl:col-span-1">
                <div className="sticky top-24">
                  <AuditSummary summaryData={summaryData || { totalEntries: auditEntries.length, actionBreakdown: [] }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Modals */}
      <AuditDetailsModal
        entry={selectedEntry}
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedEntry(null);
        }}
      />
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
        selectedCount={selectedEntries?.length}
      />
    </div>
  );
};

export default AuditTrail;
